import { create } from '@bufbuild/protobuf'
import { createConnectQueryKey, useMutation } from '@connectrpc/connect-query'
import { useQueryClient } from '@tanstack/react-query'

import { getControlQueryKey, getControlTimelineQueryKey } from '@/api/add-comment.hook'
import { getGetOngoingAssessmentQueryKey } from '@/api/assessments.hook'
import { getAssessorListQuestionnairesQueryKey } from '@/api/assessor-questionnaire.hook'
import { getListControlsQueryKey } from '@/api/control.hook'
import { ControlDataQueryKey } from '@/api/export-company.hook'
import { Company } from '@/gen/inventory/v1/company_service_pb'
import { QuestionnaireSchema } from '@/gen/questionnaire/v2/model_pb'
import { updateQuestionnaire } from '@/gen/questionnaire/v2/questionnaire-QuestionnaireService_connectquery'
import {
  ListQuestionnairesResponse,
  ListQuestionnairesResponseSchema,
  QuestionnaireService,
  UpdateQuestionnaireRequest,
} from '@/gen/questionnaire/v2/questionnaire_pb'

import { defaultTransport } from '@/lib/connect/connect-transport'

export const getListQuestionnairesQueryKey = (companyId?: Company['id']) =>
  createConnectQueryKey({
    transport: defaultTransport,
    schema: QuestionnaireService.method.listQuestionnaires,
    input: { companyId },
    cardinality: 'finite',
  })

export const useOptimisticUpdateQuestionnaire = (
  companyId?: Company['id'],
  assessmentId?: string,
) => {
  const queryClient = useQueryClient()

  return useMutation(updateQuestionnaire, {
    onMutate: async (variables) => {
      const listQuestionnairesQueryKey = getListQuestionnairesQueryKey(companyId)
      await queryClient.cancelQueries({ queryKey: listQuestionnairesQueryKey })

      const previousData = queryClient.getQueryData<ListQuestionnairesResponse>(
        listQuestionnairesQueryKey,
      )

      if (previousData) {
        const optimisticData = updateQuestionnaireData(
          previousData,
          variables as UpdateQuestionnaireRequest,
        )
        queryClient.setQueryData(listQuestionnairesQueryKey, optimisticData)
      }

      return { previousData }
    },
    onError: (_error, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(getListQuestionnairesQueryKey(companyId), context.previousData)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: getListQuestionnairesQueryKey(companyId) })
      queryClient.invalidateQueries({ queryKey: getListControlsQueryKey() })
      queryClient.invalidateQueries({ queryKey: getAssessorListQuestionnairesQueryKey() })
      queryClient.invalidateQueries({ queryKey: [ControlDataQueryKey, companyId] })
      // Sharing seeds control thread activities, so refresh the per-control
      // timeline and control queries that render them.
      queryClient.invalidateQueries({ queryKey: getControlTimelineQueryKey() })
      queryClient.invalidateQueries({ queryKey: getControlQueryKey() })
      if (assessmentId) {
        queryClient.invalidateQueries({ queryKey: getGetOngoingAssessmentQueryKey(assessmentId) })
      }
    },
  })
}

const updateQuestionnaireData = (
  oldData: ListQuestionnairesResponse,
  variables: UpdateQuestionnaireRequest,
): ListQuestionnairesResponse => {
  const { questionnaireId, status, dueDate } = variables

  const updatedQuestionnaires = oldData.questionnaires.map((q) => {
    if (q.base?.id === questionnaireId) {
      // Create a new questionnaire with updated base properties
      const updatedQuestionnaire = create(QuestionnaireSchema, {
        ...q,
        base: q.base
          ? {
              ...q.base,
              ...(status !== undefined && { status }),
              ...(dueDate !== undefined && { dueDate }),
            }
          : undefined,
      })
      return updatedQuestionnaire
    }
    return q
  })

  // Create a new response object with the updated questionnaires
  return create(ListQuestionnairesResponseSchema, {
    ...oldData,
    questionnaires: updatedQuestionnaires,
  })
}
